import { PersistedWorkflow } from '@cotera/sdk/core';
import type {
  WorkflowRunGraphEdge,
  WorkflowRunGraphNode,
} from './workflow-run.types';

const { WorkflowNodeType } = PersistedWorkflow;

export const ROOT_RUN_INSTANCE_KEY = 'root';

export type ExpandedRunGraphCard = {
  /** React Flow node id (`definitionNodeId::instanceKey`). */
  flowId: string;
  definitionNodeId: string;
  instanceKey: string;
  status: string | null;
  error: string | null;
  /** 1-based item index when this card is one of several fan-out clones. */
  itemIndex: number | null;
  node: WorkflowRunGraphNode;
};

export type ExpandedRunGraphEdge = {
  id: string;
  fromFlowId: string;
  toFlowId: string;
};

/** Stable React Flow id for a definition node + instance. */
export function makeRunFlowNodeId(
  definitionNodeId: string,
  instanceKey: string
): string {
  return `${definitionNodeId}::${instanceKey}`;
}

export type GraphCardInstance = {
  instanceKey: string;
  parentInstanceKey: string | null;
  status: string | null;
  error: string | null;
};

/**
 * Which instances become graph cards for a definition node.
 * - for-each: keep a single parent/root card (loop body fans out downstream)
 * - body nodes with non-root instances: one card per iteration
 * - otherwise: root / sole instance, or a synthetic root placeholder
 */
export function selectInstancesForGraphCards(
  node: WorkflowRunGraphNode
): GraphCardInstance[] {
  const instances = node.instances ?? [];

  if (node.type === WorkflowNodeType.ForEach) {
    const root = instances.find(
      (instance) => instance.instanceKey === ROOT_RUN_INSTANCE_KEY
    );
    if (root !== undefined) {
      return [root];
    }
    return [
      {
        instanceKey: ROOT_RUN_INSTANCE_KEY,
        parentInstanceKey: null,
        status: node.status,
        error: node.error,
      },
    ];
  }

  const nonRoot = instances.filter(
    (instance) => instance.instanceKey !== ROOT_RUN_INSTANCE_KEY
  );
  if (nonRoot.length > 0) {
    return nonRoot;
  }

  if (instances.length === 1) {
    const sole = instances[0];
    if (sole !== undefined) {
      return [sole];
    }
  }

  return [
    {
      instanceKey: ROOT_RUN_INSTANCE_KEY,
      parentInstanceKey: null,
      status: node.status,
      error: node.error,
    },
  ];
}

function expandDefinitionEdge(params: {
  edge: WorkflowRunGraphEdge;
  fromCards: readonly ExpandedRunGraphCard[];
  toCards: readonly ExpandedRunGraphCard[];
}): ExpandedRunGraphEdge[] {
  const { edge, fromCards, toCards } = params;
  if (fromCards.length === 0 || toCards.length === 0) {
    return [];
  }

  if (fromCards.length === 1 && toCards.length === 1) {
    const from = fromCards[0];
    const to = toCards[0];
    if (from === undefined || to === undefined) {
      return [];
    }
    return [
      {
        id: edge.id,
        fromFlowId: from.flowId,
        toFlowId: to.flowId,
      },
    ];
  }

  if (fromCards.length === 1 && toCards.length > 1) {
    const from = fromCards[0];
    if (from === undefined) {
      return [];
    }
    return toCards.map((to) => ({
      id: `${edge.id}::${to.instanceKey}`,
      fromFlowId: from.flowId,
      toFlowId: to.flowId,
    }));
  }

  if (fromCards.length > 1 && toCards.length === 1) {
    const to = toCards[0];
    if (to === undefined) {
      return [];
    }
    return fromCards.map((from) => ({
      id: `${edge.id}::${from.instanceKey}`,
      fromFlowId: from.flowId,
      toFlowId: to.flowId,
    }));
  }

  // Both sides fanned: wire matching instance keys (same for-each iteration).
  const toByKey = new Map(
    toCards.map((card) => [card.instanceKey, card] as const)
  );
  const matched: ExpandedRunGraphEdge[] = [];
  for (const from of fromCards) {
    const to = toByKey.get(from.instanceKey);
    if (to === undefined) {
      continue;
    }
    matched.push({
      id: `${edge.id}::${from.instanceKey}`,
      fromFlowId: from.flowId,
      toFlowId: to.flowId,
    });
  }
  return matched;
}

/**
 * Expands definition nodes/edges into execution cards so for-each body steps
 * fan out horizontally under TB dagre layout.
 *
 * Fan-in topology (body/leaf → fan-in) is expected from the run-details API
 * via `shapeRunGraphForDisplay`; this only expands instance cards onto those
 * edges.
 */
export function expandRunGraphForFanOut(
  nodes: readonly WorkflowRunGraphNode[],
  edges: readonly WorkflowRunGraphEdge[]
): { cards: ExpandedRunGraphCard[]; edges: ExpandedRunGraphEdge[] } {
  const cardsByDefinitionId = new Map<string, ExpandedRunGraphCard[]>();
  const cards: ExpandedRunGraphCard[] = [];

  for (const node of nodes) {
    const instances = selectInstancesForGraphCards(node);
    const fanned = instances.length > 1;
    const nodeCards: ExpandedRunGraphCard[] = instances.map(
      (instance, index) => ({
        flowId: makeRunFlowNodeId(node.nodeId, instance.instanceKey),
        definitionNodeId: node.nodeId,
        instanceKey: instance.instanceKey,
        status: instance.status,
        error: instance.error,
        itemIndex: fanned ? index + 1 : null,
        node,
      })
    );
    cardsByDefinitionId.set(node.nodeId, nodeCards);
    cards.push(...nodeCards);
  }

  const expandedEdges: ExpandedRunGraphEdge[] = [];
  for (const edge of edges) {
    expandedEdges.push(
      ...expandDefinitionEdge({
        edge,
        fromCards: cardsByDefinitionId.get(edge.fromNodeId) ?? [],
        toCards: cardsByDefinitionId.get(edge.toNodeId) ?? [],
      })
    );
  }

  return { cards, edges: expandedEdges };
}
